import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency, formatDate, PROJECT_STATUSES, StatusBadge } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Package, ShoppingCart, Clock, CheckCircle2, XCircle, Upload, FileText, X, Euro, Calendar, FileSearch, Send, ShieldAlert, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import useFocusItem from '@/hooks/useFocusItem';

const INITIAL_ORDER = { title: '', description: '', amount_ht: 0, deadline: '' };
const INITIAL_RFQ = { description: '', quantity: '', desired_deadline: '', general_tolerance: '', surface_finish: '', production_type: '', additional_specs: '' };

const ORDER_STATUS_MAP = {
  pending: { label: 'En attente', icon: Clock, color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  accepted: { label: 'Acceptée', icon: CheckCircle2, color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  rejected: { label: 'Refusée', icon: XCircle, color: 'bg-rose-500/10 text-rose-400 border-rose-500/20' },
};

const RFQ_STATUS_MAP = {
  pending_review: { label: 'En attente de validation', icon: Clock, color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  open: { label: 'Ouverte aux ateliers', icon: Send, color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  proposition_faite: { label: 'Proposition reçue', icon: Sparkles, color: 'bg-violet-500/10 text-violet-300 border-violet-500/30' },
  client_declined: { label: 'Déclinée', icon: XCircle, color: 'bg-rose-500/10 text-rose-300 border-rose-500/30' },
  rejected_by_admin: { label: 'Non publiée', icon: ShieldAlert, color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' },
  closed: { label: 'Clôturée', icon: CheckCircle2, color: 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20' },
};

export default function ClientPortalPage() {
  const { user } = useAuth();
  const [productions, setProductions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderDialog, setOrderDialog] = useState(false);
  const [orderForm, setOrderForm] = useState(INITIAL_ORDER);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  // RFQ state
  const [priceRequests, setPriceRequests] = useState([]);
  useFocusItem(priceRequests);
  const [rfqDialog, setRfqDialog] = useState(false);
  const [rfqForm, setRfqForm] = useState(INITIAL_RFQ);
  const [rfqFiles, setRfqFiles] = useState([]);
  const [rfqUploading, setRfqUploading] = useState(false);
  const [submittingRfq, setSubmittingRfq] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [prodsRes, ordersRes, meRes, rfqRes] = await Promise.all([
        api.get('/client/productions'),
        api.get('/client/orders'),
        api.get('/client/me'),
        api.get('/price-requests/client-mine').catch(() => ({ data: [] })),
      ]);
      setProductions(prodsRes.data);
      setOrders(ordersRes.data);
      setProfile(meRes.data);
      setPriceRequests(rfqRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNewOrder = () => {
    setOrderForm(INITIAL_ORDER);
    setUploadedFiles([]);
    setOrderDialog(true);
  };

  const uploadOrderFile = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      const newFiles = [];
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('entity_type', 'client_order');
        fd.append('entity_id', 'new');
        const { data } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        newFiles.push(data);
      }
      setUploadedFiles(prev => [...prev, ...newFiles]);
      toast.success('Fichier(s) ajouté(s)');
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur d'upload"); }
    finally { setUploading(false); }
  };

  const removeUpload = async (fileId) => {
    try {
      await api.delete(`/files/${fileId}`);
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    } catch { /* ignore */ }
  };

  const submitOrder = async () => {
    if (!orderForm.title.trim()) { toast.error('Titre requis'); return; }
    try {
      const payload = {
        title: orderForm.title,
        description: orderForm.description,
        amount_ht: parseFloat(orderForm.amount_ht) || 0,
        deadline: orderForm.deadline,
        attachments: uploadedFiles.map(f => f.id),
        purchase_order_file_id: uploadedFiles[0]?.id || null,
      };
      await api.post('/client/orders', payload);
      toast.success('Commande envoyée à ProdLink');
      setOrderDialog(false);
      setOrderForm(INITIAL_ORDER);
      setUploadedFiles([]);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  // ---------- RFQ (price request) handlers ----------
  const openNewRfq = () => {
    setRfqForm(INITIAL_RFQ);
    setRfqFiles([]);
    setRfqDialog(true);
  };

  const uploadRfqFile = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setRfqUploading(true);
    try {
      const newFiles = [];
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('entity_type', 'price_request');
        fd.append('entity_id', 'new');
        const { data } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        newFiles.push(data);
      }
      setRfqFiles(prev => [...prev, ...newFiles]);
      toast.success(`${newFiles.length} fichier(s) ajouté(s)`);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur d'upload"); }
    finally { setRfqUploading(false); }
  };

  const removeRfqFile = async (fileId) => {
    try {
      await api.delete(`/files/${fileId}`);
      setRfqFiles(prev => prev.filter(f => f.id !== fileId));
    } catch { /* ignore */ }
  };

  const submitRfq = async () => {
    if (!rfqForm.description.trim()) { toast.error('Description requise'); return; }
    setSubmittingRfq(true);
    try {
      await api.post('/price-requests/client-submit', {
        description: rfqForm.description,
        quantity: rfqForm.quantity,
        desired_deadline: rfqForm.desired_deadline || null,
        attachments: rfqFiles.map(f => f.id),
        general_tolerance: rfqForm.general_tolerance,
        surface_finish: rfqForm.surface_finish,
        production_type: rfqForm.production_type,
        additional_specs: rfqForm.additional_specs,
      });
      toast.success('Demande envoyée ! ProdLink la validera avant transmission aux ateliers.');
      setRfqDialog(false);
      setRfqForm(INITIAL_RFQ);
      setRfqFiles([]);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSubmittingRfq(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const inProgressCount = productions.filter(p => !['livre', 'archive'].includes(p.status)).length;
  const totalValue = productions.reduce((s, p) => s + (p.amount_ht || 0), 0);
  const pendingOrders = orders.filter(o => o.status === 'pending').length;

  return (
    <div className="space-y-6" data-testid="client-portal">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Bonjour, {user?.name || profile?.contact_name || 'Client'}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">{profile?.company_name || ''} · Espace client ProdPilot</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="new-rfq-btn" onClick={openNewRfq} variant="outline" className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10 rounded-sm font-medium">
            <FileSearch className="w-4 h-4 mr-2" /> Demander un devis
          </Button>
          <Button data-testid="new-order-btn" onClick={openNewOrder} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
            <ShoppingCart className="w-4 h-4 mr-2" /> Passer commande
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="kpi-productions">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-500/10 rounded-sm flex items-center justify-center"><Package className="w-4 h-4 text-blue-400" /></div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Productions en cours</p>
                <p className="text-xl font-bold text-blue-400" style={{fontFamily:'Cabinet Grotesk'}}>{inProgressCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="kpi-total">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-emerald-500/10 rounded-sm flex items-center justify-center"><Euro className="w-4 h-4 text-emerald-400" /></div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Montant total HT</p>
                <p className="text-lg font-bold text-emerald-400" style={{fontFamily:'Cabinet Grotesk'}}>{formatCurrency(totalValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="kpi-pending">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-amber-500/10 rounded-sm flex items-center justify-center"><Clock className="w-4 h-4 text-amber-400" /></div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Commandes en attente</p>
                <p className="text-xl font-bold text-amber-400" style={{fontFamily:'Cabinet Grotesk'}}>{pendingOrders}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="kpi-rfq">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-violet-500/10 rounded-sm flex items-center justify-center"><FileSearch className="w-4 h-4 text-violet-400" /></div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Demandes de prix</p>
                <p className="text-xl font-bold text-violet-400" style={{fontFamily:'Cabinet Grotesk'}}>{priceRequests.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="productions" className="space-y-4">
        <TabsList className="bg-zinc-900 border border-zinc-800 rounded-sm">
          <TabsTrigger value="productions" data-testid="tab-productions" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-amber-500">Productions en cours</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-amber-500">Mes commandes</TabsTrigger>
          <TabsTrigger value="rfq" data-testid="tab-rfq" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-amber-500">
            Mes demandes de prix
            {priceRequests.filter(p => p.status === 'pending_review').length > 0 && (
              <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-500 text-zinc-950 font-bold">
                {priceRequests.filter(p => p.status === 'pending_review').length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="productions">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Référence</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Projet</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Statut</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Montant HT</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Livraison prévue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {productions.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-12 text-zinc-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-zinc-700" />
                    Aucune production rattachée pour le moment
                  </TableCell></TableRow>
                ) : productions.map(p => (
                  <TableRow key={p.id} className="border-zinc-800/50" data-testid={`prod-row-${p.id}`}>
                    <TableCell className="text-xs text-zinc-400 font-mono">{p.reference}</TableCell>
                    <TableCell className="text-sm text-zinc-200 font-medium">{p.project_name}</TableCell>
                    <TableCell><StatusBadge status={p.status} statusMap={PROJECT_STATUSES} /></TableCell>
                    <TableCell className="text-sm text-emerald-400 font-medium">{formatCurrency(p.amount_ht)}</TableCell>
                    <TableCell className="text-sm text-zinc-300"><Calendar className="w-3 h-3 inline mr-1 text-zinc-500" />{p.delivery_date ? formatDate(p.delivery_date) : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Titre</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Montant HT</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Délai</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Bon de commande</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Statut</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Créée le</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-zinc-500">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-zinc-700" />
                    Aucune commande passée. Cliquez sur "Passer commande" pour en créer une.
                  </TableCell></TableRow>
                ) : orders.map(o => {
                  const st = ORDER_STATUS_MAP[o.status] || ORDER_STATUS_MAP.pending;
                  const Icon = st.icon;
                  return (
                    <TableRow key={o.id} className="border-zinc-800/50" data-testid={`order-row-${o.id}`}>
                      <TableCell className="text-sm text-zinc-200 font-medium">{o.title}<div className="text-[11px] text-zinc-500 truncate max-w-sm">{o.description}</div></TableCell>
                      <TableCell className="text-sm text-emerald-400 font-medium">{formatCurrency(o.amount_ht)}</TableCell>
                      <TableCell className="text-sm text-zinc-300">{o.deadline || '-'}</TableCell>
                      <TableCell className="text-xs">
                        {o.attachments?.length ? (
                          <span className="text-blue-400"><FileText className="w-3 h-3 inline mr-0.5" />{o.attachments.length} fichier(s)</span>
                        ) : <span className="text-zinc-600">-</span>}
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium border ${st.color}`}>
                          <Icon className="w-3 h-3 mr-1" /> {st.label}
                        </span>
                        {o.admin_notes && <p className="text-[10px] text-zinc-500 mt-1 italic">"{o.admin_notes}"</p>}
                      </TableCell>
                      <TableCell className="text-sm text-zinc-500">{formatDate(o.created_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="rfq">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Description</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Quantité</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Délai souhaité</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Fichiers</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Devis reçus</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Statut</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Créée le</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {priceRequests.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-zinc-500">
                    <FileSearch className="w-12 h-12 mx-auto mb-3 text-zinc-700" />
                    Aucune demande de prix.
                    <div className="text-xs text-zinc-600 mt-1">Cliquez "Demander un devis" pour soumettre vos plans à notre réseau d'ateliers.</div>
                  </TableCell></TableRow>
                ) : priceRequests.map(rfq => {
                  const st = RFQ_STATUS_MAP[rfq.status] || RFQ_STATUS_MAP.pending_review;
                  const Icon = st.icon;
                  return (
                    <TableRow key={rfq.id} data-focus-id={rfq.id} className="border-zinc-800/50" data-testid={`rfq-row-${rfq.id}`}>
                      <TableCell className="text-sm text-zinc-200 max-w-md">
                        <div className="font-medium line-clamp-2">{rfq.description}</div>
                        {rfq.rejected_reason && rfq.status === 'rejected_by_admin' && (
                          <div className="text-[11px] text-rose-400 mt-1 italic">"{rfq.rejected_reason}"</div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-zinc-300">{rfq.quantity || '-'}</TableCell>
                      <TableCell className="text-sm text-zinc-300">{rfq.desired_deadline ? formatDate(rfq.desired_deadline) : '-'}</TableCell>
                      <TableCell className="text-xs">
                        {(rfq.attachments_meta || []).length > 0 ? (
                          <span className="text-blue-400"><FileText className="w-3 h-3 inline mr-0.5" />{(rfq.attachments_meta || []).length}</span>
                        ) : <span className="text-zinc-600">-</span>}
                      </TableCell>
                      <TableCell className="text-sm text-violet-400 font-medium">{rfq.response_count || 0}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium border ${st.color}`}>
                          <Icon className="w-3 h-3 mr-1" /> {st.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-zinc-500">{formatDate(rfq.created_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Order Dialog */}
      <Dialog open={orderDialog} onOpenChange={setOrderDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-xl max-h-[90vh] overflow-y-auto" data-testid="order-dialog">
          <DialogHeader>
            <DialogTitle>Passer une nouvelle commande</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">Renseignez les détails. ProdLink validera puis lancera votre production.</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-sm">Titre *</Label>
              <Input data-testid="order-title" value={orderForm.title} onChange={(e) => setOrderForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: Commande pièces alu Juin" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-sm">Montant HT (€)</Label>
                <Input data-testid="order-amount" type="number" step="0.01" value={orderForm.amount_ht} onChange={(e) => setOrderForm(f => ({ ...f, amount_ht: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-sm">Délai souhaité</Label>
                <Input data-testid="order-deadline" value={orderForm.deadline} onChange={(e) => setOrderForm(f => ({ ...f, deadline: e.target.value }))} placeholder="Ex: Fin juin ou 2026-06-30" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-sm">Description / Notes</Label>
              <Textarea data-testid="order-description" value={orderForm.description} onChange={(e) => setOrderForm(f => ({ ...f, description: e.target.value }))} placeholder="Quantités, spécifications, urgences..." className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm min-h-[100px]" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Bon de commande / Documents</Label>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 cursor-pointer transition-colors">
                  <Upload className="w-3.5 h-3.5" /> Téléverser
                  <input data-testid="order-file-input" type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" className="hidden" onChange={(e) => uploadOrderFile(e.target.files)} disabled={uploading} />
                </label>
                {uploading && <span className="text-[11px] text-zinc-500 animate-pulse">Envoi...</span>}
              </div>
              {uploadedFiles.length > 0 && (
                <div className="space-y-1 mt-2">
                  {uploadedFiles.map(f => (
                    <div key={f.id} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-sm px-2.5 py-1.5">
                      <span className="text-xs text-zinc-300 truncate"><FileText className="w-3 h-3 inline mr-1 text-blue-400" />{f.original_filename}</span>
                      <button onClick={() => removeUpload(f.id)} className="text-zinc-600 hover:text-rose-400">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderDialog(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Annuler</Button>
            <Button data-testid="submit-order" onClick={submitOrder} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
              <Plus className="w-4 h-4 mr-1" /> Envoyer la commande
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RFQ (Price Request) Dialog */}
      <Dialog open={rfqDialog} onOpenChange={setRfqDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rfq-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSearch className="w-5 h-5 text-blue-400" />
              Nouvelle demande de prix
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              Décrivez votre besoin et joignez vos plans. ProdLink validera votre demande avant de la transmettre à son réseau d'ateliers spécialisés.
            </DialogDescription>
          </DialogHeader>

          <div className="my-2 flex items-start gap-2 text-xs bg-blue-500/10 border border-blue-500/30 text-blue-200 rounded-sm px-3 py-2">
            <ShieldAlert className="w-4 h-4 shrink-0 text-blue-400 mt-0.5" />
            <div>
              <strong>Confidentialité :</strong> n'indiquez pas vos coordonnées personnelles dans la description.
              Vos infos de contact sont déjà connues de ProdLink et ne seront jamais partagées avec les ateliers.
            </div>
          </div>

          <div className="py-2 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-sm">Description du besoin *</Label>
              <Textarea
                data-testid="rfq-description"
                value={rfqForm.description}
                onChange={(e) => setRfqForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Ex: Usinage de 50 pièces en aluminium 6061-T6 selon le plan ci-joint, tolérance générale ISO 2768-mK..."
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm min-h-[120px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-sm">Quantité</Label>
                <Input
                  data-testid="rfq-quantity"
                  value={rfqForm.quantity}
                  onChange={(e) => setRfqForm(f => ({ ...f, quantity: e.target.value }))}
                  placeholder="Ex: 50 pièces, 1 prototype + 100 en série..."
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-sm">Délai souhaité</Label>
                <Input
                  data-testid="rfq-deadline"
                  type="date"
                  value={rfqForm.desired_deadline}
                  onChange={(e) => setRfqForm(f => ({ ...f, desired_deadline: e.target.value }))}
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-sm">Tolérance générale</Label>
                <Input
                  data-testid="rfq-tolerance"
                  value={rfqForm.general_tolerance}
                  onChange={(e) => setRfqForm(f => ({ ...f, general_tolerance: e.target.value }))}
                  placeholder="ISO 2768-mK, ±0.1mm..."
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-sm">État de surface</Label>
                <Input
                  data-testid="rfq-surface"
                  value={rfqForm.surface_finish}
                  onChange={(e) => setRfqForm(f => ({ ...f, surface_finish: e.target.value }))}
                  placeholder="Ra 1.6, anodisation noire..."
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-sm">Type de production</Label>
              <select
                data-testid="rfq-production-type"
                value={rfqForm.production_type}
                onChange={(e) => setRfqForm(f => ({ ...f, production_type: e.target.value }))}
                className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 rounded-sm h-9 px-3 text-sm"
              >
                <option value="">— Précisez —</option>
                <option value="prototype">Prototype</option>
                <option value="preserie">Présérie</option>
                <option value="serie">Série</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-sm">Spécifications complémentaires</Label>
              <Textarea
                data-testid="rfq-additional"
                value={rfqForm.additional_specs}
                onChange={(e) => setRfqForm(f => ({ ...f, additional_specs: e.target.value }))}
                placeholder="Traitement thermique, certifications, packaging, etc."
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm min-h-[70px]"
              />
            </div>

            <div className="space-y-2 pt-2 border-t border-zinc-800">
              <Label className="text-zinc-400 text-sm">Plans / fichiers 3D</Label>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-300 hover:text-zinc-100 hover:border-blue-500/40 cursor-pointer transition-colors">
                  <Upload className="w-3.5 h-3.5" /> Téléverser plans/3D
                  <input
                    data-testid="rfq-file-input"
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.dxf,.dwg,.step,.stp,.iges,.igs,.stl,.obj,.x_t,.x_b,.sldprt,.sldasm,.ipt"
                    className="hidden"
                    onChange={(e) => uploadRfqFile(e.target.files)}
                    disabled={rfqUploading}
                  />
                </label>
                {rfqUploading && <span className="text-[11px] text-zinc-500 animate-pulse">Envoi...</span>}
              </div>
              <div className="text-[11px] text-zinc-600">Formats acceptés : PDF, PNG/JPG, DXF/DWG, STEP/IGES/STL/SLDPRT…</div>
              {rfqFiles.length > 0 && (
                <div className="space-y-1 mt-2">
                  {rfqFiles.map(f => (
                    <div key={f.id} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-sm px-2.5 py-1.5">
                      <span className="text-xs text-zinc-300 truncate"><FileText className="w-3 h-3 inline mr-1 text-blue-400" />{f.original_filename}</span>
                      <button onClick={() => removeRfqFile(f.id)} className="text-zinc-600 hover:text-rose-400" data-testid={`rfq-remove-${f.id}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRfqDialog(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Annuler</Button>
            <Button
              data-testid="submit-rfq"
              onClick={submitRfq}
              disabled={submittingRfq}
              className="bg-blue-600 text-white hover:bg-blue-500 rounded-sm font-medium"
            >
              {submittingRfq ? <Clock className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Envoyer ma demande
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
